import {
  Grid,
  Icon,
  ServiceIcon,
  SkeletonBlock,
  SkeletonText,
  Spacer,
  Tag,
  Text,
  TooltipWrapper,
} from "@zapier/design-system";
import {
  ZapMetadataStep,
  getUniqueAppsFromSteps,
  zapNoteAsyncAtomFamily,
} from "@zapier/canvas-state";
import classNames from "classnames";
import { Suspense, useCallback, useState } from "react";
import { useAtomValue } from "jotai";

import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import { StepsList } from "../../components/StepsList";
import formatAppsWithLimit from "../../utils/formatAppsWithLimit";
import isNumber from "../../utils/isNumber";
import getAppIconUrl from "../../utils/getAppIconUrl";
import useServiceById from "../../hooks/useServiceById";
import cleanUpAppId from "../../utils/cleanUpAppId";

import styles from "./ZapPreview.module.css";
import { ZapLink } from "../ZapLink";

type Props = {
  title?: string;
  steps: ZapMetadataStep[];
  tagLabel?: string;
  isLoadingZap?: boolean;
  isDarkMode?: boolean;
  zapId?: string;
  hasZapNotes?: boolean;
};

const ZapPreview = ({
  steps,
  title,
  tagLabel,
  isLoadingZap,
  isDarkMode,
  zapId,
  hasZapNotes,
}: Props) => {
  const uniqueApps = getUniqueAppsFromSteps(steps);
  const limitedApps = formatAppsWithLimit(uniqueApps);
  const getService = useServiceById();
  const { emitEventOnAssetOpen } = useEmitUserInteractionEvents();

  if (isLoadingZap) {
    return (
      <div
        className={`${styles.zapPreview} ${styles.loading} ${
          isDarkMode ? styles.zapPreviewDark : ""
        }`}
      >
        <div className={styles.zapPreviewIconsRow}>
          {[{ id: "1" }, { id: "2" }].map((step) => (
            <span key={step.id}>
              <SkeletonBlock borderRadius="medium" height="40px" width="40px" />
            </span>
          ))}
        </div>
        <SkeletonText textAlign="left" lines={1} size="heading" width="100%" />
        <Spacer height={10} />
        <SkeletonText textAlign="left" lines={2} size="heading" width="100%" />
      </div>
    );
  }

  return (
    <div
      data-testid="zapPreview"
      className={`${styles.zapPreview} ${
        isDarkMode ? styles.zapPreviewDark : ""
      }`}
    >
      <div className={styles.zapPreviewIconsRow}>
        <ul className={styles.limitedApps}>
          {limitedApps.map((appId, index) => {
            const service = getService(appId);
            const images = service?.images;
            const size = 40;

            // If app is a number, it's the middle app count
            const serviceName = isNumber(appId)
              ? `Plus ${appId} more apps`
              : (service?.name ?? cleanUpAppId(appId as string));

            return (
              <li
                key={index}
                className={classNames({
                  [styles.zapPreviewIconWrapper]: true,
                  [styles.zapPreviewIconWrapperDark]: isDarkMode,
                })}
              >
                <TooltipWrapper
                  position="northwest"
                  content={serviceName}
                  key={appId}
                >
                  {() =>
                    isNumber(appId) ? (
                      <span className={styles.middleApp}>
                        <Text type="Body3">+{appId}</Text>
                      </span>
                    ) : (
                      <ServiceIcon
                        alt={`${serviceName} icon`}
                        hasBorder={false}
                        size={size}
                        hasShadow={false}
                        serviceName={serviceName}
                        src={getAppIconUrl(appId as string, images, size)}
                      />
                    )
                  }
                </TooltipWrapper>
              </li>
            );
          })}
        </ul>
        {tagLabel && (
          <div className={styles.zapPreviewTag}>
            <Tag variant="warning">
              <Grid alignItems="center" columns="auto max-content" gap={5}>
                <Icon color="GrayWarm10" name="alertTriangle" size={20} />
                <Text type="Body3">{tagLabel}</Text>
              </Grid>
            </Tag>
          </div>
        )}
        {!isDarkMode && zapId && (
          <ZapLink
            zapId={zapId}
            onClick={() =>
              emitEventOnAssetOpen({ asset: "Zap", location: "sidebar" })
            }
          />
        )}
      </div>
      <Text
        type="ParagraphHeader2"
        color={isDarkMode ? "PrimeWhite" : "GrayWarm10"}
      >
        {title}
      </Text>
      <Spacer height={10} />
      {hasZapNotes && !isDarkMode && zapId && (
        <ZapNotesSuspended zapId={zapId} />
      )}
      <StepsList
        steps={steps}
        isDarkMode={isDarkMode}
        tooltipPosition="northwest"
      />
    </div>
  );
};

export default ZapPreview;

function ZapNotes({ zapId }: { zapId: string }) {
  const [notesCollapsed, setNotesCollapsed] = useState(true);
  const zapNotes = useAtomValue(zapNoteAsyncAtomFamily(zapId));

  const handleExpandCollapseNotes = useCallback(() => {
    setNotesCollapsed((prev) => !prev);
  }, []);

  const longNoteLength = Boolean(zapNotes && zapNotes?.length > 58);
  return (
    <div className={styles.zapNotesWrapper}>
      <Text type="SmallPrint1">
        <span className={classNames({ [styles.truncate]: notesCollapsed })}>
          {zapNotes}
        </span>
      </Text>
      {longNoteLength && (
        <button
          onClick={handleExpandCollapseNotes}
          className={styles.viewZapNotes}
        >
          {notesCollapsed ? "more" : "less"}
        </button>
      )}
    </div>
  );
}

function ZapNotesSuspended({ zapId }: { zapId: string }) {
  return (
    <Suspense fallback={<ZapNotesLoading />}>
      <ZapNotes zapId={zapId} />
    </Suspense>
  );
}

function ZapNotesLoading() {
  return (
    <div className={styles.zapNotesWrapper}>
      <SkeletonText
        textAlign="left"
        lines={1}
        size="body"
        width="100%"
        color="GrayWarm3"
      />
    </div>
  );
}
